// src/features/units/MatchingViewer.js
import React, { useState, useEffect } from 'react';
import './MatchingViewer.css';

const MatchingViewer = ({
    unit,
    userAnswers,
    showFeedback,
    onAnswerSelect,
    onCheckAnswer,
    onMarkCompleted,
    onResetUnit
}) => {
    const [content, setContent] = useState(null);
    const [leftItems, setLeftItems] = useState([]);
    const [rightItems, setRightItems] = useState([]);
    const [matches, setMatches] = useState({});
    const [selectedLeft, setSelectedLeft] = useState(null);
    const [hasAnswered, setHasAnswered] = useState(false);
    const [score, setScore] = useState({ correct: 0, total: 0 });

    const shuffleArray = (arr) => {
        const a = [...arr];
        for (let i = a.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [a[i], a[j]] = [a[j], a[i]];
        }
        return a;
    };

    const buildItems = (pairs) => {
        const leftList = pairs.map((pair, index) => ({
            id: pair.id,
            text: pair.left,
            correctMatch: pair.id,
            position: index + 1
        }));
        const rightList = shuffleArray(
            pairs.map(pair => ({ id: pair.id, text: pair.right }))
        );
        return { leftList, rightList };
    };

    useEffect(() => {
        if (!unit?.content) return;

        setMatches({});
        setSelectedLeft(null);
        setHasAnswered(false);
        setScore({ correct: 0, total: 0 });

        try {
            const parsed = typeof unit.content === 'string'
                ? JSON.parse(unit.content)
                : unit.content;

            setContent(parsed);

            if (parsed.pairs && parsed.pairs.length > 0) {
                const { leftList, rightList } = buildItems(parsed.pairs);
                setLeftItems(leftList);
                setRightItems(rightList);
            }
        } catch (error) {
            console.error('Error parsing matching question content:', error);
            setContent({ instructions: 'Error loading question', pairs: [] });
        }
    }, [unit?.content]);

    useEffect(() => {
        if (userAnswers && userAnswers[unit?.id]) {
            const previousAnswer = userAnswers[unit.id];
            if (previousAnswer.matches) {
                setMatches(previousAnswer.matches);
                setHasAnswered(true);
                if (showFeedback) calculateScore(previousAnswer.matches);
            }
        }
    }, [userAnswers, unit?.id, showFeedback]);

    const handleReset = () => {
        if (onResetUnit && unit?.id) {
            onResetUnit(unit.id);
            setMatches({});
            setSelectedLeft(null);
            setHasAnswered(false);
            setScore({ correct: 0, total: 0 });

            if (content?.pairs && content.pairs.length > 0) {
                const { leftList, rightList } = buildItems(content.pairs);
                setLeftItems(leftList);
                setRightItems(rightList);
            }
        }
    };

    const calculateScore = (matchesToCheck = matches) => {
        if (!content?.pairs || Object.keys(matchesToCheck).length === 0) {
            const result = { correct: 0, total: content?.pairs?.length || 0 };
            setScore(result);
            return result;
        }
        let correct = 0;
        const total = content.pairs.length;
        Object.entries(matchesToCheck).forEach(([leftId, rightId]) => {
            if (leftId === rightId) correct++;
        });
        const result = { correct, total };
        setScore(result);
        return result;
    };

    const handleLeftItemClick = (leftItem) => {
        if (hasAnswered) return;
        setSelectedLeft(prev => prev?.id === leftItem.id ? null : leftItem);
    };

    const handleRightItemClick = (rightItem) => {
        if (hasAnswered || !selectedLeft) return;

        const newMatches = { ...matches };
        // Remove any previous match for this left item
        delete newMatches[selectedLeft.id];
        // Remove any previous match pointing to this right item
        Object.keys(newMatches).forEach(leftId => {
            if (newMatches[leftId] === rightItem.id) delete newMatches[leftId];
        });
        newMatches[selectedLeft.id] = rightItem.id;

        setMatches(newMatches);
        setSelectedLeft(null);
    };

    const handleRemoveMatch = (leftId) => {
        if (hasAnswered) return;
        const newMatches = { ...matches };
        delete newMatches[leftId];
        setMatches(newMatches);
    };

    const handleResetMatches = () => {
        if (hasAnswered) return;
        setMatches({});
        setSelectedLeft(null);
    };

    const handleSubmitAnswer = () => {
        const scoreResult = calculateScore();
        setHasAnswered(true);
        if (onAnswerSelect) onAnswerSelect(unit.id, { matches });
        const isFullyCorrect = scoreResult.correct === scoreResult.total;
        if (onCheckAnswer) onCheckAnswer(unit.id, { matches }, isFullyCorrect);
        if (onMarkCompleted) onMarkCompleted(unit.id, null, isFullyCorrect ? 'correct' : 'wrong');
    };

    const getMatchedRightItem = (leftId) => rightItems.find(item => item.id === matches[leftId]);
    const isRightItemMatched = (rightId) => Object.values(matches).includes(rightId);
    const isMatchCorrect = (leftId, rightId) => leftId === rightId;

    if (!content) {
        return <div className="matching-content"><p>Loading matching question...</p></div>;
    }

    return (
        <div className="matching-content">

            {/* Question stem from admin */}
            {content.instructions && (
                <div
                    className="matching-stem"
                    dangerouslySetInnerHTML={{ __html: content.instructions }}
                />
            )}

            {/* Minimal tap hint — no text, just icon — disappears once first match made */}
            {!hasAnswered && Object.keys(matches).length === 0 && selectedLeft === null && (
                <div className="matching-hint">
                    👆 Tap a word, then tap its match
                </div>
            )}

            {/* Selected item prompt */}
            {selectedLeft && (
                <div className="matching-selected-prompt">
                    <strong>{selectedLeft.text}</strong> → tap its match
                </div>
            )}

            {/* Score (after answering) */}
            {hasAnswered && showFeedback && (
                <div className="matching-score">
                    <span className="score-correct">{score.correct}</span>
                    {' / '}
                    <span className="score-total">{score.total}</span>
                </div>
            )}

            {/* Two-column grid */}
            <div className="matching-container">

                {/* Left column */}
                <div className="matching-left-column">
                    <div className="matching-column-header">Items to Match</div>
                    {leftItems.map((leftItem) => {
                        const matchedRight = getMatchedRightItem(leftItem.id);
                        const isMatched = !!matchedRight;
                        const isSelected = selectedLeft?.id === leftItem.id;
                        const isCorrect = hasAnswered && showFeedback && isMatched &&
                            isMatchCorrect(leftItem.id, matchedRight.id);
                        const isIncorrect = hasAnswered && showFeedback && isMatched &&
                            !isMatchCorrect(leftItem.id, matchedRight.id);

                        return (
                            <div
                                key={leftItem.id}
                                className={`matching-item${isSelected ? ' selected' : ''}${isMatched ? ' matched' : ''}${isCorrect ? ' correct' : ''}${isIncorrect ? ' incorrect' : ''}`}
                                onClick={() => handleLeftItemClick(leftItem)}
                                role="button"
                                tabIndex={hasAnswered ? -1 : 0}
                                aria-label={`${leftItem.text}${isMatched ? `, matched with ${matchedRight.text}` : ', tap to select'}`}
                            >
                                <div className="matching-item-content">
                                    {/* No number prefix */}
                                    <span className="item-text">{leftItem.text}</span>
                                </div>
                                {isMatched && (
                                    <div className="match-indicator">
                                        {/* Show matched text instead of letter reference */}
                                        ✓ {matchedRight.text}
                                        {!hasAnswered && (
                                            <button
                                                className="remove-match"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRemoveMatch(leftItem.id);
                                                }}
                                                aria-label="Remove match"
                                            >
                                                ×
                                            </button>
                                        )}
                                    </div>
                                )}
                                {hasAnswered && showFeedback && (
                                    <div className="feedback-indicator">
                                        {isCorrect ? '✓' : isIncorrect ? '✗' : '—'}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Right column */}
                <div className="matching-right-column">
                    <div className="matching-column-header">Match Options</div>
                    {rightItems.map((rightItem) => {
                        const isMatched = isRightItemMatched(rightItem.id);
                        const canMatch = selectedLeft && !hasAnswered;

                        return (
                            <div
                                key={rightItem.id}
                                className={`matching-target${isMatched ? ' occupied' : ''}${canMatch ? ' can-drop' : ''}`}
                                onClick={() => handleRightItemClick(rightItem)}
                                role="button"
                                tabIndex={hasAnswered ? -1 : 0}
                                aria-label={`${rightItem.text}${isMatched ? ', already matched' : canMatch ? ', tap to match' : ''}`}
                            >
                                <div className="matching-target-content">
                                    {/* No letter prefix */}
                                    <span className="item-text">{rightItem.text}</span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Action buttons */}
            {!hasAnswered && (
                <div className="matching-actions">
                    {Object.keys(matches).length > 0 && (
                        <button className="reset-matches-button" onClick={handleResetMatches}>
                            Reset All Matches
                        </button>
                    )}
                    {Object.keys(matches).length === leftItems.length && (
                        <button className="check-answer-button" onClick={handleSubmitAnswer}>
                            Submit Answer
                        </button>
                    )}
                </div>
            )}

            {/* Feedback */}
            {hasAnswered && showFeedback && (
                <div className={`feedback-container ${score.correct === score.total ? 'correct' : 'incorrect'}`}>
                    <div className="feedback-header">
                        {score.correct === score.total ? '✅' : score.correct > 0 ? '🔶' : '❌'}
                    </div>
                    <div className="feedback-text">
                        {score.correct === score.total
                            ? 'Excellent! You matched all items correctly.'
                            : `You got ${score.correct} out of ${score.total} correct. Review the highlighted items.`
                        }
                    </div>
                    {content.generalFeedback && (
                        <div className="general-feedback">{content.generalFeedback}</div>
                    )}
                    <div style={{ textAlign: 'center', marginTop: '15px' }}>
                        <button
                            className="reset-button"
                            onClick={handleReset}
                            title="Try again"
                            style={{
                                padding: '8px 12px', fontSize: '18px',
                                backgroundColor: '#6c757d', color: 'white',
                                border: 'none', borderRadius: '50%', cursor: 'pointer',
                                width: '40px', height: '40px',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                margin: '0 auto'
                            }}
                        >
                            🔄
                        </button>
                    </div>
                </div>
            )}

            {/* Pairs summary after answering */}
            {hasAnswered && showFeedback && (
                <div className="matching-summary">
                    <h4>Your Matches:</h4>
                    {Object.entries(matches).map(([leftId, rightId]) => {
                        const leftItem = leftItems.find(item => item.id === leftId);
                        const rightItem = rightItems.find(item => item.id === rightId);
                        const correct = isMatchCorrect(leftId, rightId);
                        return (
                            <div key={leftId} className={`matching-pair ${correct ? 'correct' : 'incorrect'}`}>
                                <div className="matching-pair-left">{leftItem?.text}</div>
                                <div className="matching-pair-arrow">→</div>
                                <div className="matching-pair-right">{rightItem?.text}</div>
                                <div className="pair-feedback">{correct ? '✓' : '✗'}</div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default MatchingViewer;